/**
 * Single source of truth for Scaffcalc's public contact data (NAP).
 *
 * The contact page, the site-wide footer and the Organization JSON-LD all read
 * from here. Structured data that does not match the visible page is a Google
 * policy violation and can trigger a manual action, so every number in the
 * schema must also be rendered somewhere a visitor can see it — keeping the
 * values in one module is what makes that guarantee hold.
 */

export const COMPANY = {
  name: "Scaffcalc",
  legalName: "Scaffcalc AB",
  url: "https://scaffcalc.com/",
  logo: "https://scaffcalc.com/images/logo.png",
  streetAddress: "Lasarettsgatan 6",
  postalCode: "411 19",
  /**
   * Always "Göteborg", never "Gothenburg". The locality is part of the NAP key:
   * if the site says Gothenburg while Bolagsverket and Hitta say Göteborg,
   * search engines can resolve them as two separate entities. Marketing copy is
   * free to write Gothenburg — this structured block is not.
   */
  addressLocality: "Göteborg",
  addressCountry: "SE",
  registrationNumber: "559229-1362",
  vatNumber: "SE559229136201",
  foundingDate: "2019-12-06",
  /** Victor's line, used as the general number in the footer and the schema. */
  generalPhone: "+46 708 77 00 75",
  generalPhoneE164: "+46708770075",
  generalEmail: "support@scaffcalc.com"
} as const;

/** Mirrors the social links rendered in the footer; reused as schema sameAs. */
export const SOCIAL_PROFILES = [
  "https://www.linkedin.com/company/scaffcalc-ab/",
  "https://www.facebook.com/scaffcalc.se",
  "https://www.instagram.com/scaffcalc.se/",
  "https://www.youtube.com/@scaffcalc"
] as const;

export interface ContactPerson {
  name: string;
  /** Role titles stay in English on every locale, as on the about page. */
  role: string;
  /** Display form, spaced for readability. Omitted when the line is private. */
  phone?: string;
  /** E.164, used for the tel: href and for the schema. */
  phoneE164?: string;
  email: string;
  photo: string;
  /** Key into the Contact.languages message group. */
  languagesKey: "svEn" | "deSvEn";
  /** Absent for people who are not exposed as a schema contactPoint. */
  schema?: {
    contactType: "sales" | "customer service";
    areaServed: readonly string[];
    availableLanguage: readonly string[];
  };
}

export const CONTACT_PEOPLE: readonly ContactPerson[] = [
  {
    name: "Anton Boså",
    role: "Sales Manager",
    phone: "+46 721 56 97 23",
    phoneE164: "+46721569723",
    email: "anton.b@scaffcalc.com",
    photo: "/images/team/anton-bosa.webp",
    languagesKey: "svEn",
    schema: {
      contactType: "sales",
      areaServed: ["SE", "NO", "DK", "FI", "NL", "BE"],
      availableLanguage: ["Swedish", "English"]
    }
  },
  {
    name: "David Gyllensten",
    role: "Head of Sales, DACH",
    phone: "+46 705 61 14 16",
    phoneE164: "+46705611416",
    email: "david@scaffcalc.com",
    photo: "/images/team/david-gyllensten.webp",
    languagesKey: "deSvEn",
    schema: {
      contactType: "sales",
      areaServed: ["DE", "AT", "CH"],
      availableLanguage: ["German", "English"]
    }
  },
  {
    name: "Victor Johansson",
    role: "Sales Development Representative",
    phone: "+46 708 77 00 75",
    phoneE164: "+46708770075",
    email: "victor@scaffcalc.com",
    photo: "/images/team/victor-johansson.webp",
    languagesKey: "svEn",
    schema: {
      contactType: "customer service",
      areaServed: ["SE", "NO", "DK", "FI"],
      availableLanguage: ["Swedish", "English"]
    }
  },
  {
    /**
     * No phone number, by design. Phone support is a paid tier, so a public
     * support line would promise something we do not sell at that level. She is
     * listed with name, role, photo and email only — and consequently carries no
     * schema contactPoint either.
     */
    name: "Fanny Frostelind",
    role: "Customer Success Manager",
    email: "fanny@scaffcalc.com",
    photo: "/images/team/fanny-frostelind.webp",
    languagesKey: "svEn"
  }
];
